'use client';

import { useState, useEffect, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HomeScreen from '@/components/HomeScreen';
import StatusScreen from '@/components/StatusScreen';
import { getPersistentPlayerId, getReconnectToken } from '@/lib/socket';
import { analyticsErrorReason } from '@evoke/game-client/analytics';
import { trackEvent } from '@/lib/posthog';

const isValidNickname = (nickname: string | null | undefined): boolean => {
  return nickname !== null &&
         nickname !== undefined &&
         nickname !== 'undefined' &&
         nickname !== 'null' &&
         nickname.trim().length > 0;
};

const getSavedNickname = (): string | null => {
  try {
    const saved = localStorage.getItem('evoke-nickname');
    return isValidNickname(saved) ? saved : null;
  } catch {
    return null;
  }
};

const saveNickname = (nickname: string): void => {
  try {
    const trimmed = nickname.trim();
    if (isValidNickname(trimmed)) {
      localStorage.setItem('evoke-nickname', trimmed);
    }
  } catch {
  }
};

const cleanupInvalidNickname = (): void => {
  try {
    const saved = localStorage.getItem('evoke-nickname');
    if (!isValidNickname(saved)) {
      localStorage.removeItem('evoke-nickname');
    }
  } catch {
  }
};

function HomeContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomFromUrl = searchParams.get('room')?.toUpperCase() || '';
  const [savedSession] = useState(() => ({ nickname: getSavedNickname() || '', roomCode: localStorage.getItem('evoke-room') || '' }));
  const [nickname, setNickname] = useState(savedSession.nickname);
  const [roomCode, setRoomCode] = useState(roomFromUrl || savedSession.roomCode.toUpperCase());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(!!savedSession.nickname && !!(roomFromUrl || savedSession.roomCode));

  useEffect(() => {
    cleanupInvalidNickname();
    if (roomFromUrl && savedSession.nickname) {
      sessionStorage.setItem('evoke-entry-source', 'invite');
      sessionStorage.setItem('evoke-entry-room', roomFromUrl);
      navigate(`/room/${roomFromUrl}`, { replace: true });
      return;
    }
    if (!savedSession.nickname || !savedSession.roomCode || roomFromUrl) return;
    const controller = new AbortController();
    async function resumeRoom() {
      try {
        const apiUrl = import.meta.env.VITE_SOCKET_URL || '';
        const response = await fetch(`${apiUrl}/api/rooms/${savedSession.roomCode}`, { signal: controller.signal });
        if (response.ok && (await response.json()).exists) {
          sessionStorage.setItem('evoke-entry-source', 'resume');
          sessionStorage.setItem('evoke-entry-room', savedSession.roomCode);
          navigate(`/room/${savedSession.roomCode}`, { replace: true });
          return;
        }
        localStorage.removeItem('evoke-room');
        localStorage.removeItem('evoke-player-id');
      } catch {
        if (controller.signal.aborted) return;
      }
      setCheckingSession(false);
    }
    void resumeRoom();
    return () => controller.abort();
  }, [roomFromUrl, savedSession, navigate]);

  const handleCreateRoom = async () => {
    if (!nickname.trim()) return;
    trackEvent('room_create_requested');

    setLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_SOCKET_URL || '';
      const response = await fetch(`${apiUrl}/api/rooms/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to create room');
      }

      const data = await response.json();
      saveNickname(nickname.trim());
      localStorage.setItem('evoke-room', data.roomCode);
      sessionStorage.setItem('evoke-entry-source', 'create');
      sessionStorage.setItem('evoke-entry-room', data.roomCode);
      navigate(`/room/${data.roomCode}`);
    } catch (err) {
      trackEvent('room_create_failed', { reason: analyticsErrorReason(err instanceof Error ? err.message : undefined) });
      setError(err instanceof Error ? err.message : 'Failed to create room');
      setLoading(false);
    }
  };

  const handleJoinRoom = async () => {
    if (!nickname.trim() || !roomCode.trim()) return;
    trackEvent('room_join_requested', { entry_source: 'landing' });

    setLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_SOCKET_URL || '';
      const code = roomCode.trim().toUpperCase();
      const persistentId = getPersistentPlayerId();

      const response = await fetch(`${apiUrl}/api/rooms/${code}/validate-join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname: nickname.trim(), persistentId, reconnectToken: getReconnectToken() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join room');
      }

      saveNickname(nickname.trim());
      localStorage.setItem('evoke-room', code);
      sessionStorage.setItem('evoke-entry-source', 'landing');
      sessionStorage.setItem('evoke-entry-room', code);
      navigate(`/room/${code}`);
    } catch (err) {
      trackEvent('room_join_failed', { reason: analyticsErrorReason(err instanceof Error ? err.message : undefined), entry_source: 'landing' });
      setError(err instanceof Error ? err.message : 'Failed to join room');
      setLoading(false);
    }
  };

  if (checkingSession) return <StatusScreen title="Finding your table" message="Checking your saved game…" />;

  return <HomeScreen nickname={nickname} roomCode={roomCode} loading={loading} error={error}
    onNicknameChange={setNickname} onRoomCodeChange={setRoomCode} onCreate={handleCreateRoom} onJoin={handleJoinRoom} />;
}

export default function Home() {
  return (
    <Suspense fallback={<StatusScreen title="Pulling up a chair" message="Loading Evoke…" />}>
      <HomeContent />
    </Suspense>
  );
}
